import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { PatientResponse, MultipleCallsScheduleCreate, StrategyWithVersions } from "@/lib/types";
import { useOrganizationsStore } from "@/stores/organizationsStore";

interface ScheduleAPCMCallsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patients: PatientResponse[];
  verifiedPhoneNumberId: string;
  onSuccess?: () => void;
}

const ScheduleAPCMCallsDialog: React.FC<ScheduleAPCMCallsDialogProps> = ({
  open,
  onOpenChange,
  patients,
  verifiedPhoneNumberId,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isScheduling, setIsScheduling] = useState(false);
  const [strategies, setStrategies] = useState<StrategyWithVersions[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>("");
  const [selectedStrategyVersionId, setSelectedStrategyVersionId] = useState<string>("");
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const { selectedOrganizationId } = useOrganizationsStore();
  
  const [schedulingInfo, setSchedulingInfo] = useState<MultipleCallsScheduleCreate>({
    schedule_datetime: new Date().getFullYear() + '-' + 
      String(new Date().getMonth() + 1).padStart(2, '0') + '-' + 
      String(new Date().getDate()).padStart(2, '0') + 'T' + 
      String(new Date().getHours()).padStart(2, '0') + ':' + 
      String(new Date().getMinutes()).padStart(2, '0'),
    timezone_name: "America/Denver",
    flexible_time_window: true
  });

  // Fetch strategies when dialog opens
  useEffect(() => {
    if (open && selectedOrganizationId) {
      fetchStrategies();
    }
  }, [open, selectedOrganizationId]);

  const fetchStrategies = async () => {
    if (!selectedOrganizationId) return;
    
    try {
      setIsLoadingStrategies(true);
      const strategiesData = await api.getOrganizationStrategies(selectedOrganizationId);
      // For now, show all strategies since we don't have the exact call_type values
      // TODO: Update when we have the correct call_type enum values for consent calls
      setStrategies(strategiesData);
    } catch (error) {
      console.error("Error fetching strategies:", error);
      toast({
        title: "Error",
        description: "Failed to load strategies. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingStrategies(false);
    }
  };

  // Update the datetime every minute to keep it current
  useEffect(() => {
    const intervalId = setInterval(() => {
      setSchedulingInfo({
        ...schedulingInfo,
        schedule_datetime: new Date().getFullYear() + '-' + 
          String(new Date().getMonth() + 1).padStart(2, '0') + '-' + 
          String(new Date().getDate()).padStart(2, '0') + 'T' + 
          String(new Date().getHours()).padStart(2, '0') + ':' + 
          String(new Date().getMinutes()).padStart(2, '0')
      });
    }, 60000);

    return () => clearInterval(intervalId);
  }, [schedulingInfo]);

  const handleScheduleCalls = async () => {
    if (!selectedOrganizationId || !verifiedPhoneNumberId || patients.length === 0 || !selectedStrategyVersionId) {
      toast({
        title: "Error",
        description: "Missing required information for scheduling calls. Please select a strategy and version.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsScheduling(true);
      
      const patientIds = patients.map(patient => patient.id);
      
      await api.scheduleMultipleAPCMConsentCalls(
        patientIds,
        selectedOrganizationId,
        selectedStrategyVersionId,
        verifiedPhoneNumberId,
        schedulingInfo
      );

      toast({
        title: "Success",
        description: `APCM consent calls scheduled for ${patientIds.length} patients`,
        variant: "default",
      });

      // Call the onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error("Error scheduling APCM calls:", error);
      toast({
        title: "Error",
        description: "Failed to schedule APCM consent calls. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsScheduling(false);
    }
  };

  // Get a description of the patients being scheduled
  const getPatientsDescription = () => {
    if (patients.length > 3) {
      return `${patients.length} selected patients`;
    } else {
      return patients
        .map((p) => `${p.first_name} ${p.last_name}`)
        .join(", ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Schedule APCM Consent Calls</DialogTitle>
          <DialogDescription>
            Schedule consent calls for {getPatientsDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="strategy" className="sm:text-right">
              Strategy
            </Label>
            <Select
              value={selectedStrategyId}
              onValueChange={(value) => {
                setSelectedStrategyId(value);
                setSelectedStrategyVersionId(""); // Reset version when strategy changes
              }}
              disabled={isLoadingStrategies}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder={isLoadingStrategies ? "Loading strategies..." : "Select strategy"} />
              </SelectTrigger>
              <SelectContent>
                {strategies.map((strategy) => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    {strategy.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="strategyVersion" className="sm:text-right">
              Version
            </Label>
            <Select
              value={selectedStrategyVersionId}
              onValueChange={setSelectedStrategyVersionId}
              disabled={!selectedStrategyId}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder={!selectedStrategyId ? "Select strategy first" : "Select version"} />
              </SelectTrigger>
              <SelectContent>
                {selectedStrategyId && strategies
                  .find(s => s.id === selectedStrategyId)
                  ?.versions.map((version) => (
                    <SelectItem key={version.id} value={version.id}>
                      {version.version_description || `Version ${version.id.slice(-8)}`}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="scheduleDateTime" className="sm:text-right">
              Date & Time
            </Label>
            <Input
              id="scheduleDateTime"
              type="datetime-local"
              className="sm:col-span-3"
              value={schedulingInfo.schedule_datetime}
              onChange={(e) => {
                // Simply use the exact value from the input with no conversions
                setSchedulingInfo({
                  ...schedulingInfo,
                  schedule_datetime: e.target.value
                });
              }}
              required
            />
          </div>
          
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="timezone" className="sm:text-right">
              Timezone
            </Label>
            <Select
              value={schedulingInfo.timezone_name}
              onValueChange={(value) => setSchedulingInfo({
                ...schedulingInfo,
                timezone_name: value
              })}
            >
              <SelectTrigger className="sm:col-span-3">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">Eastern Time</SelectItem>
                <SelectItem value="America/Chicago">Central Time</SelectItem>
                <SelectItem value="America/Denver">Mountain Time</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                <SelectItem value="America/Anchorage">Alaska Time</SelectItem>
                <SelectItem value="Pacific/Honolulu">Hawaii Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center sm:gap-4">
            <Label htmlFor="flexible" className="sm:text-right">
              Flexible Time
            </Label>
            <div className="flex items-center space-x-2 col-span-3">
              <Checkbox
                id="flexible"
                checked={schedulingInfo.flexible_time_window}
                onCheckedChange={(checked) => setSchedulingInfo({
                  ...schedulingInfo,
                  flexible_time_window: checked as boolean
                })}
              />
              <label
                htmlFor="flexible"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Allow flexible timing around the scheduled time
              </label>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isScheduling}
          >
            Cancel
          </Button>
          <Button onClick={handleScheduleCalls} disabled={isScheduling}>
            {isScheduling ? "Scheduling..." : "Schedule Calls"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleAPCMCallsDialog;
